import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { workspacesApi } from '@/api/endpoints/workspaces'
import type { CreateWorkspaceData, UpdateWorkspaceData, Workspace } from '@/api/endpoints/workspaces'

/**
 * The account's workspaces. Shared by the Leads, Contacts and Organizations
 * pickers, so it is cached under one key and kept fresh for a while — the list
 * changes when a partner is onboarded, not during a browsing session.
 */
export function useWorkspaces(opts?: { includeInactive?: boolean; withCounts?: boolean }) {
  const includeInactive = opts?.includeInactive ?? true
  const withCounts = opts?.withCounts ?? false

  const query = useQuery({
    queryKey: ['workspaces', { includeInactive, withCounts }],
    queryFn: () => workspacesApi.getWorkspaces({ includeInactive, withCounts }),
    staleTime: 300_000,
  })

  const workspaces = query.data ?? []

  return {
    workspaces,
    /** Only these may receive new records; inactive ones are rejected server-side. */
    activeWorkspaces: workspaces.filter((w: Workspace) => w.status === 'active'),
    // Deliberately no `defaultWorkspace` here. It used to return the internal
    // workspace and was described as "what a record lands in when none is
    // chosen", which is how partner records ended up filed under AR Tech
    // Solutions. Where a record goes is decided by useWorkspaceSelection, which
    // returns '' when the user genuinely has to choose. If you need the internal
    // workspace as metadata, look it up explicitly at the call site and say why.
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  }
}

export function useWorkspaceMutations() {
  const qc = useQueryClient()
  const invalidate = () => qc.invalidateQueries({ queryKey: ['workspaces'] })

  const create = useMutation({
    mutationFn: (data: CreateWorkspaceData) => workspacesApi.createWorkspace(data),
    onSuccess: invalidate,
  })

  const update = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateWorkspaceData }) =>
      workspacesApi.updateWorkspace(id, data),
    onSuccess: invalidate,
  })

  const remove = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      workspacesApi.deleteWorkspace(id, reason),
    onSuccess: invalidate,
  })

  return {
    createWorkspace: create.mutateAsync,
    updateWorkspace: update.mutateAsync,
    deleteWorkspace: remove.mutateAsync,
    isMutating: create.isPending || update.isPending || remove.isPending,
  }
}
